// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import FieldDefinition, { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import Page from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import { CustomAppState } from "@sc/App/Store/Reducers";
import xBackgroundImg from "@sc/XDL/Utilities/xBackgroundImg";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    customCSS = "customCSS",
    reflow = "reflow",
    fullWidth = "fullWidth",
    fullWidthContent = "fullWidthContent",
    spaceTop = "spaceTop",
    spaceBottom = "spaceBottom",
    backgroundColor = "backgroundColor",
    backgroundImage = "backgroundImage",
    mediumBackgroundImage = "mediumBackgroundImage",
    smallBackgroundImage = "smallBackgroundImage",
    borderColor = "borderColor",
    rowId = "rowId",
    makeItemsTakeFullHeight = "makeItemsTakeFullHeight",
    // XNG-Change
    tabletGap = "tabletGap",
    useGradientBg = "useGradientBg",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.fullWidth]: boolean;
        [fields.fullWidthContent]: boolean;
        [fields.spaceTop]: string;
        [fields.spaceBottom]: string;
        [fields.backgroundColor]: string;
        [fields.backgroundImage]: string;
        [fields.mediumBackgroundImage]: string;
        [fields.smallBackgroundImage]: string;
        [fields.customCSS]: string;
        [fields.rowId]: string;
        [fields.makeItemsTakeFullHeight]: boolean;
        [fields.borderColor]: string;
        // XNG-Change
        [fields.tabletGap]: number;
        [fields.useGradientBg]: boolean;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [12, 3, 1, 1, 1],
    2: [12, 4, 2, 2, 2],
    3: [12, 6, 3, 3, 3],
    4: [12, 6, 4, 4, 4],
    5: [12, 6, 6, 5, 5],
    6: [12, 6, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const alignments = {
    top: "flex-start",
    middle: "center",
    bottom: "flex-end",
} as const;

const defaultCustomCss = `&.full-width-row {
}

&.content-size-row {
}
.column-element >div[class^="Wrapper"] {
}
`;

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            fullWidth,
            fullWidthContent,
            spaceTop,
            spaceBottom,
            backgroundColor,
            backgroundImage,
            mediumBackgroundImage,
            smallBackgroundImage,
            rowId,
            customCSS,
            makeItemsTakeFullHeight,
            borderColor,
            // XNG-Change
            tabletGap,
            useGradientBg,
        },
        id,
    } = props;

    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);

    const columnElements: JSX.Element[] = [];

    const rowIdPlacer = {
        css: css`
            position: relative;
            top: -94px;
            visibility: hidden;
            z-index: -1;
        `,
    };

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                align={align}
                css={css`
                    ${columns.length > 1 &&
                    makeItemsTakeFullHeight &&
                    ` >div[class*="Wrapper"] {
                        &, > div {
                            height: 100%;
                        }
                    }`}
                `}
                className="column-element"
            >
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    const FullWidthRow = () => (
        <StyledWrapper
            className={fullWidthContent ? "full-width-row full-width-content row" : "full-width-row row"}
            css={css`
                width: 100vw;
                position: relative;
                left: 50%;
                right: 50%;
                margin-left: -50vw;
                margin-right: -50vw;
                display: flex;
                justify-content: center;
                ${getPaddings()};
                ${getBackground()};
                ${getBorder()};
                ${customCSS}
            `}
        >
            {rowId && <StyledWrapper {...rowIdPlacer} id={rowId} />}
            <FullWidthRowContent />
        </StyledWrapper>
    );

    const FullWidthRowContent = () => {
        if (fullWidthContent) {
            return <RowGrid />;
        }

        return (
            <Page
                as="div"
                css={css`
                    &&& {
                        overflow: visible;
                    }
                `}
            >
                <RowGrid />
            </Page>
        );
    };

    const ContentSizeRow = () => (
        <StyledWrapper
            className="content-size-row row"
            css={css`
                width: 100%;
                ${getPaddings()}
                ${getBackground()}
                ${getBorder()}
                ${customCSS}
            `}
        >
            {rowId && <StyledWrapper {...rowIdPlacer} id={rowId} />}
            <RowGrid />
        </StyledWrapper>
    );

    const getBackground = () => {
        if (backgroundImage || mediumBackgroundImage || smallBackgroundImage) {
            return css`
                background-image: ${xBackgroundImg(backgroundImage)};
                background-size: cover;
                background-repeat: no-repeat;
                background-position-x: center;

                @media (max-width: 1024px) {
                    background-image: ${xBackgroundImg(
                        mediumBackgroundImage || backgroundImage || smallBackgroundImage,
                    )};
                }

                @media (max-width: 481px) {
                    background-image: ${xBackgroundImg(
                        smallBackgroundImage || backgroundImage || mediumBackgroundImage,
                    )};
                }
            `;
        }

        if (useGradientBg) {
            return css`
                background: linear-gradient(to top, ${getColor("common.ui2")} 79%, transparent 79%, transparent 100%);
                @media (max-width: 1024px) {
                    background: linear-gradient(
                        to top,
                        ${getColor("common.ui2")} 76%,
                        transparent 76%,
                        transparent 100%
                    );
                }
                @media (max-width: 767px) {
                    background: linear-gradient(
                        to top,
                        ${getColor("common.ui2")} 70%,
                        transparent 70%,
                        transparent 100%
                    );
                }
            `;
        }

        return css`
            background-color: ${backgroundColor};
        `;
    };

    const getPaddings = () => css`
        ${spaceTop && `padding-top: ${spaceTop}`};
        ${spaceBottom && `padding-bottom: ${spaceBottom}`};
    `;

    const getBorder = () => {
        if (borderColor) {
            return css`
                border: 1px solid ${borderColor};
            `;
        }
    };

    const containerGap = isTabletScreen && tabletGap ? tabletGap : gap;

    const RowGrid = () => <GridContainer gap={containerGap || 0}>{columnElements}</GridContainer>;

    return fullWidth ? <FullWidthRow /> : <ContentSizeRow />;
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const spaceFieldDefinition = (name: string) =>
    ({
        name,
        editorTemplate: "TextField",
        fieldType: "General",
        defaultValue: "",
        tooltip: "padding value in pixels",
        placeholder: "Example: 20px",
        validate: (value: string | null) => {
            if (!value) {
                return null;
            }
            return CSS.supports("padding", value) ? null : `Invalid Input`;
        },
    } as FieldDefinition);

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            // XNG-Change
            {
                name: fields.tabletGap,
                displayName: "Gap Between Items on Tablet and Mobile",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.makeItemsTakeFullHeight,
                displayName: "Show all child Columns with the same height",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.rowId,
                displayName: "Row ID",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 3,
                tab: advancedTab,
            },
            {
                name: fields.fullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.fullWidthContent,
                displayName: "Content Full Width",
                tooltip: "Content will expand to full screen with no side-paddings",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: ({ fields }) => fields.fullWidth,
            },
            {
                ...spaceFieldDefinition(fields.spaceTop),
            },
            {
                ...spaceFieldDefinition(fields.spaceBottom),
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "",
            },
            {
                name: fields.useGradientBg,
                displayName: "Use gradient background color",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.borderColor,
                editorTemplate: "ColorPickerField",
                displayName: "Border Color",
                defaultValue: "",
            },
            {
                name: fields.backgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Large Background Image",
            },
            {
                name: fields.mediumBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Medium Background Image",
            },
            {
                name: fields.smallBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Small Background Image",
            },
        ],
    },
};

export default widgetModule;
